import { useMemo, useState } from 'react';
import { Badge, Card } from 'react-bootstrap';
import {
    FaBath,
    FaBed,
    FaBuilding,
    FaCar,
    FaChargingStation,
    FaChevronDown,
    FaChevronRight,
    FaClipboardCheck,
    FaCompass,
    FaCouch,
    FaFileAlt,
    FaHome,
    FaIdCard,
    FaLayerGroup,
    FaMapMarkerAlt,
    FaParking,
    FaRulerCombined,
    FaStar,
} from 'react-icons/fa';
import './DetailInfo.css';
import {
    buildLocationText,
    formatCurrency,
    formatNumber,
    getApprovalLabel,
    getListingSourceLabel,
    getStatusLabel,
} from './detailProperty.helpers';

const displayValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const normalizeText = (value) => {
    const normalized = displayValue(value);
    return typeof normalized === 'string' ? normalized.trim() : String(normalized).trim();
};

const isValidValue = (value) => {
    const normalized = normalizeText(value);
    return normalized !== '-' && normalized !== '';
};

const uniqueBy = (items, getKey) => {
    const seen = new Set();

    return items.filter((item) => {
        const key = getKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const DetailRow = ({ icon: Icon, label, value, hint }) => {
    const normalizedValue = displayValue(value);
    const isMuted = normalizedValue === '-';

    return (
        <div className="detail-row">
            <div className="detail-row__icon" aria-hidden="true">
                <Icon />
            </div>

            <div className="detail-row__content">
                <div className="detail-row__label">{label}</div>
                <div className={`detail-row__value ${isMuted ? 'is-muted' : ''}`}>{normalizedValue}</div>
                {hint ? <div className="detail-row__hint">{hint}</div> : null}
            </div>
        </div>
    );
};

const DetailSection = ({ title, subtitle, items, visibleCount = 5 }) => {
    const [expanded, setExpanded] = useState(false);

    const hasMoreThanFive = items.length > visibleCount;
    const visibleItems = expanded ? items : items.slice(0, visibleCount);

    return (
        <section className="detail-panel">
            <div className="detail-panel__head">
                <div>
                    <h2 className="detail-panel__title">{title}</h2>
                    {subtitle ? <p className="detail-panel__subtitle">{subtitle}</p> : null}
                </div>
            </div>

            <div className="detail-panel__body">
                <div className="detail-list">
                    {visibleItems.map((item) => (
                        <DetailRow
                            key={`${item.label}-${normalizeText(item.value)}`}
                            icon={item.icon}
                            label={item.label}
                            value={item.value}
                            hint={item.hint}
                        />
                    ))}
                </div>
            </div>

            {hasMoreThanFive ? (
                <div className="detail-panel__footer">
                    <button
                        type="button"
                        className="detail-panel__action"
                        onClick={() => setExpanded((prev) => !prev)}
                        aria-expanded={expanded}
                    >
                        <span>{expanded ? 'Tampilkan Lebih Sedikit' : 'Lihat Lebih Banyak'}</span>
                        {expanded ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                    </button>
                </div>
            ) : null}
        </section>
    );
};

const DetailInfo = ({ property, className = '' }) => {
    const locationText = useMemo(() => buildLocationText(property), [property]);

    const metaChips = useMemo(() => {
        const rawChips = [
            property?.status_label || getStatusLabel(property?.status),
            property?.approval_label || getApprovalLabel(property?.approval_status),
            property?.property_type?.name || '-',
            property?.certificate_type?.name || '-',
            getListingSourceLabel(property?.listing_source),
        ].filter(isValidValue);

        return uniqueBy(rawChips, (item) => normalizeText(item).toLowerCase());
    }, [property]);

    const priceText = property?.price_format || formatCurrency(property?.price);

    const quickFacts = useMemo(
        () =>
            uniqueBy(
                [
                    {
                        icon: FaClipboardCheck,
                        label: 'Status',
                        value: property?.status_label || getStatusLabel(property?.status),
                        note: 'Status listing properti',
                    },
                    {
                        icon: FaStar,
                        label: 'Approval',
                        value: property?.approval_label || getApprovalLabel(property?.approval_status),
                        note: 'Status verifikasi data',
                    },
                    {
                        icon: FaHome,
                        label: 'Tipe',
                        value: property?.property_type?.name || '-',
                        note: 'Kategori properti',
                    },
                    {
                        icon: FaIdCard,
                        label: 'Sertifikat',
                        value: property?.certificate_type?.name || '-',
                        note: 'Jenis legalitas',
                    },
                ],
                (item) => `${normalizeText(item.label).toLowerCase()}__${normalizeText(item.value).toLowerCase()}`
            ).filter((item) => isValidValue(item.value)),
        [property]
    );

    const propertyRows = useMemo(
        () =>
            uniqueBy(
                [
                    {
                        icon: FaBuilding,
                        label: 'Tipe Properti',
                        value: property?.property_type?.name,
                        hint: 'Kategori utama listing.',
                    },
                    {
                        icon: FaFileAlt,
                        label: 'Sertifikat',
                        value: property?.certificate_type?.name,
                        hint: 'Jenis dokumen kepemilikan.',
                    },
                    {
                        icon: FaLayerGroup,
                        label: 'Listing Source',
                        value: getListingSourceLabel(property?.listing_source),
                        hint: 'Sumber data properti.',
                    },
                    {
                        icon: FaCompass,
                        label: 'Orientasi',
                        value: property?.building_orientation,
                        hint: 'Arah hadap bangunan.',
                    },
                    {
                        icon: FaBuilding,
                        label: 'Kondisi',
                        value: property?.building_condition,
                        hint: 'Kondisi fisik saat ini.',
                    },
                    {
                        icon: FaCouch,
                        label: 'Perabotan',
                        value: property?.furnishing_status,
                        hint: 'Status kelengkapan interior.',
                    },
                ],
                (item) => `${normalizeText(item.label).toLowerCase()}__${normalizeText(item.value).toLowerCase()}`
            ).filter((item) => isValidValue(item.value)),
        [property]
    );

    const stats = useMemo(
        () =>
            uniqueBy(
                [
                    {
                        icon: FaRulerCombined,
                        label: 'Luas Tanah',
                        value: `${formatNumber(property?.land_area)} m²`,
                        hint: 'Total area tanah.',
                    },
                    {
                        icon: FaRulerCombined,
                        label: 'Luas Bangunan',
                        value: `${formatNumber(property?.building_area)} m²`,
                        hint: 'Total area bangunan.',
                    },
                    {
                        icon: FaBed,
                        label: 'Kamar Tidur',
                        value: formatNumber(property?.bedrooms),
                        hint: 'Jumlah ruang tidur.',
                    },
                    {
                        icon: FaBath,
                        label: 'Kamar Mandi',
                        value: formatNumber(property?.bathrooms),
                        hint: 'Jumlah kamar mandi.',
                    },
                    {
                        icon: FaBuilding,
                        label: 'Lantai',
                        value: formatNumber(property?.floors),
                        hint: 'Jumlah level bangunan.',
                    },
                    {
                        icon: FaParking,
                        label: 'Garasi',
                        value: formatNumber(property?.garage_count),
                        hint: 'Kapasitas garasi.',
                    },
                    {
                        icon: FaCar,
                        label: 'Carport',
                        value: formatNumber(property?.carport_count),
                        hint: 'Kapasitas carport.',
                    },
                    {
                        icon: FaChargingStation,
                        label: 'Daya Listrik',
                        value: property?.electric_power ? `${formatNumber(property.electric_power)} Watt` : '-',
                        hint: 'Kapasitas listrik terpasang.',
                    },
                    {
                        icon: FaBuilding,
                        label: 'Tahun Dibangun',
                        value: formatNumber(property?.construction_year),
                        hint: 'Estimasi tahun bangunan.',
                    },
                    {
                        icon: FaBuilding,
                        label: 'Tahun Renovasi',
                        value: formatNumber(property?.renovation_year),
                        hint: 'Riwayat renovasi terakhir.',
                    },
                ],
                (item) => `${normalizeText(item.label).toLowerCase()}__${normalizeText(item.value).toLowerCase()}`
            ).filter((item) => isValidValue(item.value)),
        [property]
    );

    return (
        <Card className={`detail-card detail-card--premium ${className}`.trim()}>
            <Card.Body className="detail-card-body">
                <div className="detail-hero">
                    <div className="detail-hero__content">
                        <div className="detail-price-inline">
                            <span className="detail-price-label">Harga Listing</span>
                            <div className="detail-price">{priceText}</div>
                            <div className="detail-price-subtitle">
                                <FaStar aria-hidden="true" />
                                <span>Informasi harga properti terkini</span>
                            </div>
                        </div>

                        <div className="detail-info-badges">
                            {metaChips.map((chip) => (
                                <Badge key={chip} bg="light" text="dark" className="detail-chip detail-chip--soft">
                                    {chip}
                                </Badge>
                            ))}
                        </div>

                        <h1 className="detail-title">{property?.title || 'Properti'}</h1>

                        <p className="detail-location">
                            <FaMapMarkerAlt aria-hidden="true" />
                            <span>{locationText || 'Lokasi belum tersedia'}</span>
                        </p>
                    </div>
                </div>

                <div className="detail-fact-strip" aria-label="Ringkasan properti">
                    {quickFacts.map((item) => (
                        <div className="detail-fact" key={`${item.label}-${item.value}`}>
                            <div className="detail-fact__icon" aria-hidden="true">
                                <item.icon />
                            </div>
                            <div className="detail-fact__body">
                                <div className="detail-fact__label">{item.label}</div>
                                <div className="detail-fact__value">{item.value || '-'}</div>
                                <div className="detail-fact__note">{item.note}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="detail-panels">
                    <DetailSection
                        title="Informasi Properti"
                        subtitle="Ringkasan utama listing dan identitas properti"
                        items={propertyRows}
                        visibleCount={5}
                    />

                    <DetailSection
                        title="Spesifikasi Teknis"
                        subtitle="Detail fisik bangunan"
                        items={stats}
                        visibleCount={5}
                    />
                </div>
            </Card.Body>
        </Card>
    );
};

export default DetailInfo;
